import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import { invitationService } from "@/services/InvitationService";
import { motion } from "framer-motion";

export default function InviteeQuestions() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [contributorName, setContributorName] = useState("");
  const [contributorRelationship, setContributorRelationship] = useState("");
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    // Store the user data and existing questions from the verification response
    const verificationData = JSON.parse(localStorage.getItem('verificationData'));
    if (verificationData) {
      console.log('Loading verification data:', verificationData);
      setUserData(verificationData.user);
      if (verificationData.data) {
        // Transform the existing questions into our format
        const existingQuestions = verificationData.data.map(q => ({
          id: q.id,
          question: q.question,
          questionNumber: q.question_number || 1
        }));
        console.log('Loaded existing questions:', existingQuestions);
        setQuestions(existingQuestions);
      }
      setContributorName(verificationData.user.name || '');
    }
  }, []);

  const updateQuestion = (index, value) => {
    // Immediate console log of the typing
    console.log(`Typing in question ${index + 1}:`, {
      value: value,
      existingId: questions[index]?.id || 'NEW'
    });

    setQuestions(prev => {
      const newQuestions = [...prev];
      // If the question exists, update it, otherwise create a new one
      if (newQuestions[index]) {
        newQuestions[index] = {
          ...newQuestions[index],
          question: value
        };
      } else {
        newQuestions[index] = {
          question: value,
          questionNumber: index + 1
        };
      }
      return newQuestions;
    });
  };

  const submitQuestions = async (e) => {
    e.preventDefault();
    
    try {
      const submissionData = {
        questions: questions.map(q => ({
          ...(q.id && { id: q.id }), // Only include id if it exists
          question: q.question,
          question_number: q.questionNumber
        })),
        contributor: {
          name: contributorName,
          relationship: contributorRelationship
        }
      };

      if (userData) {
        submissionData.email = userData.email;
      }
      
      console.log('Submitting questions data:', {
        questions: submissionData.questions.map(q => ({
          id: q.id || 'NEW',
          question: q.question,
          question_number: q.question_number
        })),
        contributor: submissionData.contributor,
        email: userData?.email
      });
      
      await invitationService.submitQuestions(submissionData);
      navigate('/thank-you-invitee');
    } catch (error) {
      console.error('Error submitting questions:', error);
      // Handle error appropriately
    }
  };

  const questionPrompts = [
    "What's a childhood memory of John that always makes you smile?",
    "What life lesson from John has had the biggest impact on you?",
    "What would you want future generations to know about John's character?",
    "Is there a story about John that you think should be preserved forever?",
    "What question would you most want to ask John about their life?"
  ];

  const placeholders = [
    "Share a specific memory or story that captures who John was as a child...",
    "Think about advice, wisdom, or an example John set that changed how you think or act...",
    "Describe the qualities that make John special and worth remembering...",
    "Share a story that captures something important about John's life or personality...",
    "Think about something you've always wondered about John's experiences, dreams, or perspectives..."
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Header with person's info */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-20 h-20 bg-primary rounded-lg flex items-center justify-center mx-auto mb-6">
            <Heart className="text-primary-foreground text-2xl" />
          </div>
          <h1 className="text-4xl font-bold text-foreground mb-4">Help Create John&apos;s Legacy Story</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            John has invited you to contribute meaningful questions for their legacy recording. 
            Your questions will help capture the stories that matter most to your family.
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-secondary rounded-xl border border-secondary-border p-8"
        >
          <form onSubmit={submitQuestions}>
            <div className="space-y-8">
              {questionPrompts.map((prompt, index) => (
                <div key={index} className="question-item">
                  <label className="block text-[18px] font-semibold text-foreground mb-3">
                    {prompt}
                  </label>
                  <textarea 
                    className="w-full px-4 py-3 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-[18px] font-semibold placeholder:text-sm placeholder:text-[#7C797980] min-h-[120px] resize-none" 
                    placeholder={placeholders[index]}
                    value={questions[index]?.question || ''}
                    onChange={(e) => updateQuestion(index, e.target.value)}
                  />
                </div>
              ))}
            </div>

            {/* Contributor Info */}
            <div className="mt-8 pt-6 border-t border-secondary-border">
              <h4 className="text-[18px] font-semibold text-foreground mb-4">Your Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Your Name</label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-2 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-[18px] font-semibold placeholder:text-sm placeholder:text-[#7C797980]" 
                    placeholder="How should we credit you?"
                    value={contributorName}
                    onChange={(e) => setContributorName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Your Relationship</label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-2 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-[18px] font-semibold placeholder:text-sm placeholder:text-[#7C797980]" 
                    placeholder="e.g., Daughter, Best Friend, Colleague"
                    value={contributorRelationship}
                    onChange={(e) => setContributorRelationship(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="mt-8">
              <button 
                type="submit" 
                className="w-full py-3 bg-primary text-primary-foreground rounded-[4px] hover:bg-primary/90 transition-colors text-[18px] font-semibold"
              >
                Submit My Questions
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
} 